import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard, Building2, Users, MessageSquare, HelpCircle, 
  DollarSign, BarChart3, Settings, LogOut, ChevronLeft, ChevronRight,
  Crown, Archive, CreditCard, FileText, Bell, UsersRound, ClipboardCheck,
  Target, Receipt, FileCheck, Calendar, Sliders, School, Briefcase, Tag, 
  Megaphone, Mail, TrendingUp, Cross, Gift, ChevronDown, BookOpen, Printer,
  GraduationCap, Star, Search, Palette, Video, Share2, X as XIcon
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';

const FAV_KEY = 'zazu-admin-favorites';

const CollapsibleGroup = ({ label, icon: Icon, children, collapsed, defaultOpen = false, iconClass = "" }) => {
  const [open, setOpen] = useState(defaultOpen);

  if (collapsed) {
    return <>{children}</>;
  }

  return (
    <div className="mb-1">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 text-[11px] font-semibold text-slate-400 uppercase tracking-wider hover:text-slate-600 transition-colors"
        data-testid={`sidebar-group-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {Icon && <Icon className={cn("w-3.5 h-3.5", iconClass)} />}
        <span className="flex-1 text-left">{label}</span>
        <ChevronDown className={cn("w-3.5 h-3.5 transition-transform duration-200", open && "rotate-180")} />
      </button>
      <div className={cn(
        "overflow-hidden transition-all duration-200",
        open ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
      )}>
        {children}
      </div>
    </div>
  );
};

const SidebarItem = ({ item, activeSection, setActiveSection, collapsed, favorites, toggleFav, onNavigate }) => {
  const isActive = activeSection === item.id;
  const isFav = favorites?.includes(item.id);
  const handleClick = () => {
    if (item.href) {
      window.location.href = item.href;
    } else {
      setActiveSection(item.id);
    }
    if (onNavigate) onNavigate();
  };
  return (
    <div className="group relative">
      <button
        onClick={handleClick}
        className={cn(
          "w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-all text-sm",
          isActive
            ? "bg-teal-700 text-white shadow-sm"
            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        )}
        data-testid={`sidebar-${item.id}`}
      >
        <item.icon className={cn("w-4 h-4 shrink-0", collapsed && "mx-auto")} />
        {!collapsed && <span className="flex-1 text-left">{item.label}</span>}
        {item.badge > 0 && (
          <span className={cn(
            "bg-rose-500 text-white text-[10px] font-bold rounded-full",
            collapsed ? "absolute top-0 right-0 w-4 h-4 flex items-center justify-center" : "px-2 py-0.5"
          )}>
            {item.badge}
          </span>
        )}
      </button>
      {!collapsed && toggleFav && (
        <button
          onClick={(e) => { e.stopPropagation(); toggleFav(item.id); }}
          className={cn(
            "absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity",
            isFav && "opacity-100"
          )}
          aria-label="Pin to favorites"
        >
          <Star className={cn("w-3.5 h-3.5", isFav ? "fill-amber-400 text-amber-400" : "text-slate-400")} />
        </button>
      )}
    </div>
  );
};

const PlatformAdminSidebar = ({ collapsed, setCollapsed, activeSection, setActiveSection, pendingRequests = 0, mobileOpen = false, onMobileClose }) => {
  const { logout, user } = useAuth();
  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem(FAV_KEY)) || ['overview', 'organizations', 'billing']; }
    catch { return ['overview', 'organizations', 'billing']; }
  });

  // Interns / staff without admin see a filtered sidebar — no billing, no org management,
  // no revenue, no CRM. They keep access to their own work: Newsletters, Support Inbox,
  // Team Chat, Team, Careers, plus a link back to their tasks.
  const isAdmin = !!user?.is_admin;
  const internRole = user?.intern_role;
  const isIntern = !isAdmin && (!!internRole || !!user?.is_staff);

  const INTERN_ALLOWED_ITEMS = {
    social_media:       new Set(['newsletters', 'announcements', 'employee-center', 'team-chat', 'team', 'social-hub', 'resources']),
    marketing:          new Set(['newsletters', 'announcements', 'promo-codes', 'referrals', 'employee-center', 'team-chat', 'team', 'social-hub', 'resources']),
    content_curriculum: new Set(['newsletters', 'faq', 'employee-center', 'team-chat', 'team', 'social-hub', 'resources']),
    support:            new Set(['support', 'faq', 'newsletters', 'employee-center', 'team-chat', 'team', 'resources']),
    sales:              new Set(['users', 'leads', 'quotes', 'purchase-orders', 'employee-center', 'team-chat', 'team', 'resources']),
    hr:                 new Set(['team', 'hr-inventory', 'employee-center', 'team-chat', 'careers', 'resources']),
  };
  const allowed = isIntern ? (INTERN_ALLOWED_ITEMS[internRole] || new Set()) : null;

  useEffect(() => {
    localStorage.setItem(FAV_KEY, JSON.stringify(favorites));
  }, [favorites]);

  const toggleFav = (id) => {
    setFavorites((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const groups = [
    {
      label: "Main",
      icon: LayoutDashboard,
      defaultOpen: true,
      items: [
        { id: 'overview', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'organizations', label: 'Organizations', icon: Building2 },
        { id: 'users', label: 'All Users', icon: Users },
        { id: 'license-requests', label: 'License Requests', icon: ClipboardCheck, badge: pendingRequests },
      ]
    },
    {
      label: "People & HR",
      icon: UsersRound,
      defaultOpen: true,
      items: [
        { id: 'team', label: 'Team & Org Chart', icon: UsersRound, href: '/employee/team' },
        { id: 'careers', label: 'Careers & Hiring', icon: Briefcase, href: '/admin/careers' },
        { id: 'employee-center', label: 'Employee Center', icon: BookOpen, href: '/employee/center' },
        { id: 'team-chat', label: 'Team Chat', icon: MessageSquare, href: '/employee/chat' },
        { id: 'meetings', label: 'Meetings & Calls', icon: Video, href: '/team/meetings' },
        { id: 'hr-inventory', label: 'HR Inventory', icon: ClipboardCheck, href: '/admin/hr-inventory' },
        { id: 'resources', label: 'Resources', icon: FileCheck, href: '/employee/resources' },
        { id: 'social-hub', label: 'Social Hub', icon: Megaphone, href: '/social-hub' },
      ]
    },
    {
      label: "Sales & CRM",
      icon: Briefcase,
      defaultOpen: false,
      items: [
        { id: 'crm', label: 'CRM / Leads', icon: Briefcase },
        { id: 'leads', label: 'Quick Leads', icon: Target },
        { id: 'quotes', label: 'Quotes', icon: FileText },
        { id: 'purchase-orders', label: 'Purchase Orders', icon: Receipt },
      ]
    },
    {
      label: "Marketing",
      icon: Megaphone,
      defaultOpen: false,
      items: [
        { id: 'promo-codes', label: 'Promo Codes', icon: Tag },
        { id: 'referrals', label: 'Referral Program', icon: Gift },
        { id: 'announcements', label: 'Announcements', icon: Megaphone },
        { id: 'newsletters', label: 'Newsletters', icon: Mail },
        { id: 'flyer-generator', label: 'Flyer Generator', icon: Printer },
      ]
    },
    {
      label: "Support & KB",
      icon: HelpCircle,
      defaultOpen: false,
      items: [
        { id: 'support', label: 'Support Tickets', icon: MessageSquare },
        { id: 'faq', label: 'Knowledge Base', icon: BookOpen },
      ]
    },
    {
      label: "Christian Education",
      icon: Cross,
      defaultOpen: false,
      items: [
        { id: 'christian', label: 'Christian Module', icon: Cross },
        { id: 'tier-features', label: 'Tier Features', icon: Sliders },
      ]
    },
    {
      label: "Finance & Reports",
      icon: BarChart3,
      defaultOpen: false,
      items: [
        { id: 'pricing', label: 'Pricing Plans', icon: DollarSign },
        { id: 'billing', label: 'Subscription Billing', icon: CreditCard },
        { id: 'revenue', label: 'Revenue', icon: TrendingUp },
        { id: 'scheduled-reports', label: 'Scheduled Reports', icon: Calendar },
        { id: 'reports', label: 'Reports & Analytics', icon: BarChart3 },
      ]
    },
    {
      label: "System",
      icon: Settings,
      defaultOpen: false,
      items: [
        { id: 'archived', label: 'Archived', icon: Archive },
        { id: 'themes', label: 'Branding & Themes', icon: Palette },
        { id: 'concept-images', label: 'Concept Illustrations', icon: Palette, href: '/admin/concept-images' },
        { id: 'social-links', label: 'Social Footer Links', icon: Share2, href: '/admin/social-links' },
        { id: 'settings', label: 'Settings', icon: Settings },
      ]
    },
  ];

  // Build favorites list from chosen IDs
  const allItems = groups.flatMap(g => g.items);
  const favItems = favorites.map(id => allItems.find(i => i.id === id)).filter(Boolean);

  // Sidebar menu search
  const [menuQuery, setMenuQuery] = useState('');
  const q = menuQuery.trim().toLowerCase();
  const matchesQuery = (item) => !q || (item.label || '').toLowerCase().includes(q) || (item.id || '').toLowerCase().includes(q);

  // For interns, filter each group's items to only allowed IDs and drop empty groups.
  const preVisibleGroups = !allowed
    ? groups
    : groups
        .map((g) => ({ ...g, items: g.items.filter((i) => allowed.has(i.id)) }))
        .filter((g) => g.items.length > 0);
  const visibleGroups = q
    ? preVisibleGroups
        .map((g) => ({ ...g, items: g.items.filter(matchesQuery), defaultOpen: true }))
        .filter((g) => g.items.length > 0)
    : preVisibleGroups;
  const visibleFavItems = (!allowed ? favItems : favItems.filter((i) => allowed.has(i.id)))
    .filter(matchesQuery);

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-40 md:hidden"
          onClick={onMobileClose}
          data-testid="sidebar-mobile-backdrop"
        />
      )}
      <div className={cn(
        "fixed left-0 top-0 h-screen bg-white border-r border-slate-200 text-slate-700 flex flex-col transition-transform duration-300 z-50",
        // Mobile: fixed drawer, slides in from left
        "w-72 max-w-[85vw] md:w-64",
        mobileOpen ? "translate-x-0" : "-translate-x-full",
        // Desktop: always visible, honors collapsed width
        "md:translate-x-0 md:transition-all",
        collapsed ? "md:w-16" : "md:w-64"
      )}>
      {/* Header */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl flex items-center justify-center shrink-0">
            <Crown className="w-5 h-5 text-white" />
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <h1 className="font-semibold text-slate-900 text-base leading-none">Zazu Admin</h1>
              <p className="text-xs text-slate-500 mt-1">Platform Console</p>
            </div>
          )}
          {/* Mobile close button */}
          <button
            onClick={onMobileClose}
            className="md:hidden p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
            data-testid="sidebar-mobile-close"
            aria-label="Close menu"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 overflow-y-auto">
        {/* Menu search — desktop only when expanded (or always on mobile since drawer is full width) */}
        {!collapsed && (
          <div className="mb-2 relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={menuQuery}
              onChange={(e) => setMenuQuery(e.target.value)}
              placeholder="Search menu…"
              className="w-full pl-8 pr-7 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-md placeholder:text-slate-400 focus:outline-none focus:border-emerald-400 focus:bg-white"
              data-testid="sidebar-menu-search"
            />
            {menuQuery && (
              <button
                onClick={() => setMenuQuery('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-0.5"
                aria-label="Clear menu search"
                data-testid="sidebar-menu-search-clear"
              >
                <XIcon className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}

        {/* Favorites - collapsible */}
        {!collapsed && visibleFavItems.length > 0 && (
          <CollapsibleGroup
            label="Favorites"
            icon={Star}
            collapsed={collapsed}
            defaultOpen={true}
            iconClass="fill-amber-400 text-amber-400"
          >
            <div className="space-y-0.5 pl-1">
              {visibleFavItems.map((item) => (
                <SidebarItem
                  key={`fav-${item.id}`}
                  item={item}
                  activeSection={activeSection}
                  setActiveSection={setActiveSection}
                  collapsed={collapsed}
                  favorites={favorites}
                  toggleFav={toggleFav}
                  onNavigate={onMobileClose}
                />
              ))}
            </div>
          </CollapsibleGroup>
        )}

        {/* Interns get a persistent "My tasks" shortcut back to their dashboard */}
        {isIntern && !collapsed && (
          <div className="mb-2">
            <button
              onClick={() => { window.location.href = '/intern/dashboard'; }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-sm font-medium"
              data-testid="sidebar-back-to-my-tasks"
            >
              <Star className="w-3.5 h-3.5" /> My tasks
            </button>
          </div>
        )}

        {visibleGroups.map((group) => (
          <CollapsibleGroup
            key={group.label}
            label={group.label}
            icon={group.icon}
            collapsed={collapsed}
            defaultOpen={group.defaultOpen}
          >
            <div className="space-y-0.5 pl-1">
              {group.items.map((item) => (
                <SidebarItem
                  key={item.id}
                  item={item}
                  activeSection={activeSection}
                  setActiveSection={setActiveSection}
                  collapsed={collapsed}
                  favorites={favorites}
                  toggleFav={toggleFav}
                  onNavigate={onMobileClose}
                />
              ))}
            </div>
          </CollapsibleGroup>
        ))}
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-slate-200">
        {!collapsed && (
          <div className="mb-3 px-2 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 text-white flex items-center justify-center font-semibold text-sm">
              {(user?.name || user?.email || 'A').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name || 'Admin'}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
        )}
        <Button
          variant="ghost"
          className={cn(
            "w-full text-slate-600 hover:text-slate-900 hover:bg-slate-100 justify-start",
            collapsed && "px-2 justify-center"
          )}
          onClick={logout}
          data-testid="sidebar-logout"
        >
          <LogOut className={cn("w-4 h-4", collapsed ? "mx-auto" : "mr-2")} />
          {!collapsed && "Sign out"}
        </Button>
      </div>

      {/* Collapse Toggle — desktop only */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="hidden md:flex absolute -right-3 top-20 w-6 h-6 bg-white border border-slate-200 rounded-full items-center justify-center hover:bg-slate-50 transition-colors shadow-sm"
        data-testid="sidebar-toggle"
      >
        {collapsed ? <ChevronRight className="w-3.5 h-3.5 text-slate-600" /> : <ChevronLeft className="w-3.5 h-3.5 text-slate-600" />}
      </button>
    </div>
    </>
  );
};

const OrgAdminSidebar = ({ collapsed, setCollapsed, activeSection, setActiveSection, orgName, mobileOpen = false, onMobileClose }) => {
  const { logout, user } = useAuth();

  const groups = [
    {
      label: "Overview",
      icon: LayoutDashboard,
      defaultOpen: true,
      items: [
        { id: 'overview', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'users', label: 'User Management', icon: Users },
      ]
    },
    {
      label: "Students",
      icon: School,
      defaultOpen: true,
      items: [
        { id: 'students', label: 'Students & Classes', icon: Building2 },
        { id: 'curricula', label: 'Custom Curriculum', icon: GraduationCap },
        { id: 'google-classroom', label: 'Google Classroom', icon: School },
      ]
    },
    {
      label: "Billing",
      icon: CreditCard,
      defaultOpen: false,
      items: [
        { id: 'licenses', label: 'License Management', icon: CreditCard },
        { id: 'subscription', label: 'Subscription', icon: DollarSign },
      ]
    },
    {
      label: "Resources",
      icon: BookOpen,
      defaultOpen: false,
      items: [
        { id: 'knowledge-base', label: 'Knowledge Base', icon: BookOpen },
        { id: 'reports', label: 'Reports', icon: BarChart3 },
        { id: 'notifications', label: 'Notifications', icon: Bell },
        { id: 'settings', label: 'Settings', icon: Settings },
      ]
    },
  ];

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-40 md:hidden"
          onClick={onMobileClose}
          data-testid="org-sidebar-mobile-backdrop"
        />
      )}
      <div className={cn(
        "fixed left-0 top-0 h-screen bg-white border-r border-slate-200 text-slate-700 flex flex-col transition-transform duration-300 z-50",
        "w-72 max-w-[85vw] md:w-64",
        mobileOpen ? "translate-x-0" : "-translate-x-full",
        "md:translate-x-0 md:transition-all",
        collapsed ? "md:w-16" : "md:w-64"
      )}>
      {/* Header */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-teal-600 to-teal-700 rounded-xl flex items-center justify-center shrink-0">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <h1 className="font-semibold text-slate-900 text-base truncate leading-none">{orgName || 'Org Admin'}</h1>
              <p className="text-xs text-slate-500 mt-1">Organization Console</p>
            </div>
          )}
          <button
            onClick={onMobileClose}
            className="md:hidden p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
            data-testid="org-sidebar-mobile-close"
            aria-label="Close menu"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 overflow-y-auto">
        {groups.map((group) => (
          <CollapsibleGroup
            key={group.label}
            label={group.label}
            icon={group.icon}
            collapsed={collapsed}
            defaultOpen={group.defaultOpen}
          >
            <div className="space-y-0.5 pl-1">
              {group.items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => { setActiveSection(item.id); if (onMobileClose) onMobileClose(); }}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-all text-sm",
                    activeSection === item.id
                      ? "bg-teal-700 text-white shadow-sm"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  )}
                  data-testid={`org-sidebar-${item.id}`}
                >
                  <item.icon className={cn("w-4 h-4 shrink-0", collapsed && "mx-auto")} />
                  {!collapsed && <span className="text-sm flex-1 text-left">{item.label}</span>}
                </button>
              ))}
            </div>
          </CollapsibleGroup>
        ))}
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-slate-200">
        {!collapsed && (
          <div className="mb-3 px-2 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 text-white flex items-center justify-center font-semibold text-sm">
              {(user?.name || user?.email || 'O').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name || 'Org Admin'}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
        )}
        <Button
          variant="ghost"
          className={cn(
            "w-full text-slate-600 hover:text-slate-900 hover:bg-slate-100 justify-start",
            collapsed && "px-2 justify-center"
          )}
          onClick={logout}
          data-testid="org-sidebar-logout"
        >
          <LogOut className={cn("w-4 h-4", collapsed ? "mx-auto" : "mr-2")} />
          {!collapsed && "Sign out"}
        </Button>
      </div>

      {/* Collapse Toggle — desktop only */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="hidden md:flex absolute -right-3 top-20 w-6 h-6 bg-white border border-slate-200 rounded-full items-center justify-center hover:bg-slate-50 transition-colors shadow-sm"
        data-testid="org-sidebar-toggle"
      >
        {collapsed ? <ChevronRight className="w-3.5 h-3.5 text-slate-600" /> : <ChevronLeft className="w-3.5 h-3.5 text-slate-600" />}
      </button>
    </div>
    </>
  );
};

export { PlatformAdminSidebar, OrgAdminSidebar };
